import { onMounted, ref } from 'vue'
import { useTelegram } from 'vue-tg'

const TEST_MODE = process.env.VUE_APP_TEST_MODE === 'true'
const TEST_USER_ID = process.env.VUE_APP_TEST_USER_ID || null

export function useTelegramData() {
  const userData = ref({
    firstName: '',
    lastName: '',
    photoUrl: '',
    username: '',
    id: null,
    languageCode: '',
    isPremium: false,
    isBot: false,
    authDate: null
  })

  const isLoading = ref(true)
  const isFullscreen = ref(false)
  let tg = null
  let telegram = null

  // Тестовый режим: подставляем user_id из .env и не обращаемся к Telegram
  if (TEST_MODE && TEST_USER_ID) {
    const parsedId = Number(TEST_USER_ID)
    const normalizedId = Number.isNaN(parsedId) ? TEST_USER_ID : parsedId

    userData.value = {
      ...userData.value,
      id: normalizedId,
      firstName: 'Test',
      lastName: 'User',
      username: 'test_user',
      authDate: new Date().toISOString()
    }

    isLoading.value = false

    return {
      userData,
      isLoading,
      isFullscreen,
      tg: null,
      telegram: null
    }
  }

  // Используем vue-tg для получения всех данных
  try {
    telegram = useTelegram()
    tg = telegram?.tg || null
  } catch (error) {
    console.warn('vue-tg not available:', error)
    // Fallback на прямую работу с Telegram WebApp
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      tg = window.Telegram.WebApp
    }
  }

  onMounted(() => {
    // Если tg еще не получен, пробуем снова через vue-tg
    if (!tg && !telegram) {
      try {
        telegram = useTelegram()
        tg = telegram?.tg || null
      } catch (error) {
        if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
          tg = window.Telegram.WebApp
        }
      }
    }

    // Получаем все доступные данные через vue-tg
    if (tg && tg.initDataUnsafe) {
      const initData = tg.initDataUnsafe
      const user = initData.user
      
      if (user) {
        // Получаем auth_date (дата авторизации в приложении, не дата создания аккаунта)
        let authDate = null
        if (initData.auth_date) {
          // auth_date это Unix timestamp в секундах
          authDate = new Date(initData.auth_date * 1000).toISOString()
        }
        
        // Получаем все доступные данные пользователя из Telegram Web App API
        const telegramUserData = {
          // Основные данные
          id: user.id || null,
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          username: user.username || '',
          languageCode: user.language_code || '',
          photoUrl: user.photo_url || '',
          isPremium: user.is_premium || false,
          isBot: user.is_bot || false,
          
          // Даты
          authDate: authDate // Дата авторизации в приложении
        }
        
        // Обновляем данные
        userData.value = telegramUserData
      }
    }
    
    isLoading.value = false
    
    // Проверяем режим открытия Mini App
    checkViewportMode()
  })

  // Функция для проверки режима открытия Mini App
  const checkViewportMode = () => {
    if (typeof window === 'undefined') return
    
    try {
      const telegramWebApp = window.Telegram?.WebApp
      if (!telegramWebApp) {
        // Если нет Telegram WebApp, считаем что не fullscreen
        isFullscreen.value = false
        return
      }

      const viewportHeight = telegramWebApp.viewportHeight || 0
      const windowHeight = window.innerHeight || 0
      
      // В fullscreen режиме viewportHeight должен быть близок к windowHeight
      // В compact/fullsize режимах viewportHeight значительно меньше
      const heightDiff = Math.abs(windowHeight - viewportHeight)
      
      // Fullscreen: viewportHeight близок к windowHeight (разница < 150px)
      // и viewportHeight достаточно большой (> 400px для мобильных устройств)
      // Compact обычно имеет viewportHeight около 200-300px
      // Fullsize обычно имеет viewportHeight около 500-600px
      // Fullscreen имеет viewportHeight близкий к windowHeight
      isFullscreen.value = heightDiff < 150 && viewportHeight > 400 && windowHeight > 500
    } catch (error) {
      console.warn('Error checking viewport mode:', error)
      isFullscreen.value = false
    }
  }

  // Обработчики событий для обновления режима
  let resizeHandler = null
  let visibilityHandler = null

  // Инициализируем обработчики
  if (typeof window !== 'undefined') {
    resizeHandler = () => {
      checkViewportMode()
    }
    visibilityHandler = () => {
      if (!document.hidden) {
        setTimeout(checkViewportMode, 100)
      }
    }
    
    window.addEventListener('resize', resizeHandler)
    document.addEventListener('visibilitychange', visibilityHandler)
    
    // Проверяем режим сразу
    setTimeout(checkViewportMode, 100)
  }

  return {
    userData,
    isLoading,
    isFullscreen,
    tg,
    telegram
  }
}
